/**
 * Validates that a number input string is in a valid format
 * @param inputValue - The raw input string value
 * @param allowDecimals - Whether decimal values are allowed (default: false for integers)
 * @returns Object with isValid boolean and error message if invalid
 */
export interface NumberValidationResult {
  isValid: boolean;
  error?: string;
}

export function validateNumberFormat(
  inputValue: string,
  allowDecimals: boolean = false
): NumberValidationResult {
  // Allow empty string during editing
  if (inputValue === '' || inputValue === '.') {
    return { isValid: true };
  }

  const trimmed = inputValue.trim();

  // Note: Scientific notation (e, E) is prevented by onKeyDown/onInput handlers
  // but we check here as a safeguard for edge cases

  // Check for leading zeros (e.g., "0036000", but allow "0" itself and "0.5" for decimals)
  // Allow "0" as a valid value, but not "00", "01", "00.5", etc.
  if (trimmed.length > 1 && trimmed.startsWith('0')) {
    // Allow "0." for partial decimal input (exactly "0." followed by digits)
    if (allowDecimals && trimmed[1] === '.' && trimmed.length === 2) {
      // This is fine, like "0." for typing "0.5"
    } else if (allowDecimals && trimmed[1] === '.' && trimmed.length > 2) {
      // Check if "0." is followed by digits (allow "0.5" but not "00.5")
      // If the third character is a digit, it's valid "0.5"
      // If there are more zeros before the decimal, it's invalid "00.5"
      if (!/^0\.\d/.test(trimmed)) {
        return {
          isValid: false,
          error:
            'Leading zeros are not allowed. Please enter a valid number without leading zeros.',
        };
      }
    } else {
      // Leading zero without decimal point is invalid (e.g., "0036000", "01")
      return {
        isValid: false,
        error: 'Leading zeros are not allowed. Please enter a whole number without leading zeros.',
      };
    }
  }

  // For integer fields, check for decimal point (users can paste decimals)
  if (!allowDecimals && trimmed.includes('.')) {
    return {
      isValid: false,
      error: 'Decimals are not allowed. Please enter a whole number.',
    };
  }

  // Check if it's a valid number format
  // For integers: only digits (and optional minus sign at start)
  // For decimals: digits, optional decimal point, optional minus sign at start
  const integerPattern = /^-?\d+$/;
  const decimalPattern = /^-?\d*\.?\d+$/;

  const pattern = allowDecimals ? decimalPattern : integerPattern;
  if (!pattern.test(trimmed)) {
    return {
      isValid: false,
      error: allowDecimals
        ? 'Please enter a valid number (e.g., 100 or 100.50)'
        : 'Please enter a valid whole number.',
    };
  }

  return { isValid: true };
}

/**
 * Validates and parses a number input, ensuring it matches the expected format
 * @param inputValue - The raw input string value
 * @param allowDecimals - Whether decimal values are allowed
 * @returns Object with validation result and parsed number (if valid)
 */
export function validateAndParseNumber(
  inputValue: string,
  allowDecimals: boolean = false
): NumberValidationResult & { parsedValue?: number } {
  const validation = validateNumberFormat(inputValue, allowDecimals);

  if (!validation.isValid) {
    return validation;
  }

  if (inputValue === '' || inputValue === '.') {
    return { isValid: true };
  }

  const parsed = allowDecimals ? parseFloat(inputValue) : parseInt(inputValue, 10);

  if (isNaN(parsed)) {
    return {
      isValid: false,
      error: 'Please enter a valid number.',
    };
  }

  return {
    isValid: true,
    parsedValue: parsed,
  };
}
